import { FC, useMemo } from 'react';

import { useFormikContext } from 'formik';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import { Select } from 'ui/components/form';
import { Button } from 'ui/components/common';
import { cn } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';

import { useGetPipelines, useGetStages } from '../hooks';
import { DEAL_MOVE_FORM } from './schema';

type DealMoveFormProps = {
  isLoading: boolean;
};

const DealMoveForm: FC<DealMoveFormProps> = ({ isLoading }) => {
  const { hideModal } = useModal();

  const {
    values,
    isSubmitting,
    resetForm,
    handleSubmit,
    setFieldValue,
    errors,
    isValid,
    touched,
    setFieldTouched,
  } = useFormikContext<DEAL_MOVE_FORM>();

  const { data: pipelines } = useGetPipelines();
  const { data: stages } = useGetStages(values.pipelineId);

  const stageOptions = useMemo(
    () =>
      stages?.map((stage) => ({
        value: stage?._id,
        label: stage.name,
      })) ?? [],
    [stages],
  );

  const pipelineOptions = useMemo(
    () =>
      pipelines?.map((pipeline) => ({
        value: pipeline?._id,
        label: pipeline.name,
      })) ?? [],
    [pipelines],
  );
  return (
    <form
      onSubmit={(e) => {
        handleSubmit(e);
      }}
    >
      <div className="space-y-5">
        <Select
          required
          label="Pipeline"
          value={
            pipelineOptions?.find(
              (pipeline) => pipeline.value === values.pipelineId,
            ) as unknown as string
          }
          onChange={(val: OPTIONS_PROPS) => {
            setFieldValue('pipelineId', val.value);
            setFieldTouched('pipelineId', true);
            setFieldTouched('stageId', false);
          }}
          placeholder="Select pipeline"
          className="flex-1"
          disabled={isSubmitting}
          name="pipelineId"
          options={pipelineOptions}
          textType={
            errors.pipelineId && touched.pipelineId ? 'danger' : 'default'
          }
          subText={
            errors.pipelineId && touched.pipelineId
              ? errors.pipelineId
              : undefined
          }
        />
        <Select
          required
          label="Stage"
          value={
            (stageOptions?.find(
              (stage) => stage.value === values.stageId,
            ) as any) || null
          }
          onChange={(val: OPTIONS_PROPS) => {
            setFieldValue('stageId', val.value);
            setFieldTouched('stageId', true);
          }}
          placeholder="Select stage"
          className="flex-1"
          disabled={isSubmitting}
          name="stageId"
          options={stageOptions}
          textType={errors.stageId && touched.stageId ? 'danger' : 'default'}
          subText={
            errors.stageId && touched.stageId ? errors.stageId : undefined
          }
        />
      </div>
      <div
        className={cn(
          'pt-[22px] mt-[22px]',
          'flex justify-end gap-2',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={() => {
            resetForm();
            hideModal();
          }}
          label="CANCEL"
          disabled={isSubmitting}
          intent="tertiary"
          size="sm"
        />
        <Button
          type="submit"
          label="SAVE"
          size="sm"
          disabled={!isValid || isSubmitting || isLoading}
        />
      </div>
    </form>
  );
};

export default DealMoveForm;
